import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@nesso/ui/primitives/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@nesso/ui/primitives/dialog';
import { InputField } from '@nesso/ui/primitives/input-field';

const formSchema = z.object({
    newEmail: z
        .string()
        .min(1, 'An email address is required')
        .pipe(z.email('Enter a valid email address (e.g., name@example.com)')),
});

type FormValues = z.input<typeof formSchema>;

type Props = {
    pending: boolean;
    fieldError?: string;
    onSubmit: (newEmail: string) => void;
    onCancel: () => void;
};

export function ProfileEmailChangeUpdateEmailStep({
    pending,
    fieldError,
    onSubmit,
    onCancel,
}: Props) {
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: { newEmail: '' },
        mode: 'onChange',
    });

    useEffect(() => {
        if (fieldError) form.setError('newEmail', { message: fieldError });
    }, [fieldError, form]);

    const submit = form.handleSubmit((values) => onSubmit(values.newEmail));

    return (
        <>
            <DialogHeader className="text-left">
                <DialogTitle>Update email address</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="flex flex-col gap-6">
                <Controller
                    control={form.control}
                    name="newEmail"
                    render={({ field, fieldState }) => {
                        const showError = fieldState.isTouched || form.formState.submitCount > 0;
                        return (
                            <InputField
                                {...field}
                                id="email-change-new-email"
                                label="Email address"
                                type="email"
                                autoComplete="email"
                                disabled={pending}
                                error={showError ? fieldState.error?.message : undefined}
                            />
                        );
                    }}
                />
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={pending}>
                        {pending ? 'Updating…' : 'Update Email Address'}
                    </Button>
                </DialogFooter>
            </form>
        </>
    );
}
